import type { MisconceptionDefinition } from '../types';

/**
 * TAA — Lezen sub-registry.
 *
 * Misconcepties rond tekstbegrip en lezen: kerngedachte, details, feit
 * vs mening, doel van de tekst, samenvatten, tekststructuur,
 * verbanden, verwijswoorden, voorspellen en conclusies.
 * Families: `TAA-LEZEN-*`.
 *
 * Onderdeel van de TAA-barrel-splitsing (juni 2026).
 */
export const TAA_LEZEN_MISCONCEPTIONS: MisconceptionDefinition[] = [
// ─── Feit vs mening — basis ─────────────────────────────────────────────
{
  id: 'taa.fact-vs-opinion',
  label: 'Feit vs. mening',
  module: 'Taalbegrip',
  description: 'Leerling kan geen onderscheid maken tussen feiten en meningen in een tekst.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
},
// ─── Kerngedachte (TAA-LEZEN-KERN) ──────────────────────────────────────
{
  id: 'taa.detail-as-main-idea',
  label: 'Detail aanzien voor hoofdgedachte',
  module: 'Taalbegrip',
  description: 'Leerling verwart een detail of gevolg met de hoofdgedachte van de tekst.',
  typicalFamilies: ['TAA-LEZEN-KERN']
}, {
  id: 'taa.outside-knowledge',
  label: 'Eigen kennis invullen i.p.v. tekst lezen',
  module: 'Taalbegrip',
  description: 'Leerling baseert antwoord op eigen kennis of mening in plaats van op de tekst.',
  typicalFamilies: ['TAA-LEZEN-KERN', 'TAA-LEZEN-DETAIL']
}, {
  id: 'taa.adding-to-source',
  label: 'Informatie toevoegen die niet in de tekst staat',
  module: 'Taalbegrip',
  description: 'Leerling kiest een antwoord dat informatie bevat die niet in de brontekst staat.',
  typicalFamilies: ['TAA-LEZEN-KERN']
}, {
  id: 'taa.exaggeration',
  label: 'Overdrijving van een detail uit de tekst',
  module: 'Taalbegrip',
  description: 'Leerling kiest een antwoord dat een detail uit de tekst overdrijft.',
  typicalFamilies: ['TAA-LEZEN-KERN']
}, {
  id: 'taa.opposite-meaning',
  label: 'Tegenovergestelde van wat de tekst zegt',
  module: 'Taalbegrip',
  description: 'Leerling kiest een antwoord dat het tegenovergestelde beweert van wat de tekst aangeeft.',
  typicalFamilies: ['TAA-LEZEN-KERN']
}, {
  id: 'taa.partial-reading',
  label: 'Alleen een deel van de tekst gelezen',
  module: 'Taalbegrip',
  description: 'Leerling baseert antwoord op slechts een deel van de tekst (alleen positief of negatief).',
  typicalFamilies: ['TAA-LEZEN-KERN']
}, {
  id: 'taa.title-too-vague',
  label: 'Titel te vaag — dekt de kern niet',
  module: 'Taalbegrip',
  description: 'Leerling kiest een titel die te breed of vaag is om de inhoud te dekken.',
  typicalFamilies: ['TAA-LEZEN-KERN']
},
// ─── Detail (TAA-LEZEN-DETAIL) ──────────────────────────────────────────
{
  id: 'taa.wrong-number-from-text',
  label: 'Verkeerd getal uit tekst gekozen',
  module: 'Taalbegrip',
  description: 'Leerling koppelt het verkeerde getal aan de gevraagde informatie (bijv. bouwjaar i.p.v. hoogte).',
  typicalFamilies: ['TAA-LEZEN-DETAIL']
}, {
  id: 'taa.imprecise-reading',
  label: 'Tekst onnauwkeurig gelezen',
  module: 'Taalbegrip',
  description: 'Leerling leest een getal of woord onnauwkeurig (bijv. 300 i.p.v. 330).',
  typicalFamilies: ['TAA-LEZEN-DETAIL']
}, {
  id: 'taa.synonym-confusion',
  label: 'Verwart vergelijkbare maar verschillende termen',
  module: 'Taalbegrip',
  description: 'Leerling verwart een term met een vergelijkbaar maar ander woord (bijv. blaasgat vs. neus).',
  typicalFamilies: ['TAA-LEZEN-DETAIL']
}, {
  id: 'taa.misleading-detail',
  label: 'Afleidende informatie uit tekst',
  module: 'Taalbegrip',
  description: 'Leerling laat zich afleiden door een detail dat lijkt op het antwoord maar het niet is (bijv. heviger ≠ meer).',
  typicalFamilies: ['TAA-LEZEN-DETAIL']
}, {
  id: 'taa.answer-not-in-text',
  label: 'Antwoord niet in tekst',
  module: 'Taalbegrip',
  description: 'Leerling kiest een antwoord dat niet in de tekst wordt genoemd.',
  typicalFamilies: ['TAA-LEZEN-DETAIL']
}, {
  id: 'taa.confuses-old-and-modern',
  label: 'Verwart eerste en moderne variant',
  module: 'Taalbegrip',
  description: 'Leerling verwart informatie over de oorspronkelijke en moderne versie van iets.',
  typicalFamilies: ['TAA-LEZEN-DETAIL']
}, {
  id: 'taa.wrong-question-type',
  label: 'Antwoordt op verkeerd vraagtype',
  module: 'Taalbegrip',
  description: 'Leerling beantwoordt een waar-vraag met wanneer of hoe vaak, of andersom.',
  typicalFamilies: ['TAA-LEZEN-DETAIL']
},
// ─── Feit / mening — uitgebreid (TAA-LEZEN-FEIT) ────────────────────────
{
  id: 'taa.feit-mening-regels-als-feiten',
  label: 'Verwart spelregels (feiten) met meningen',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat controleerbare spelregels meningen zijn.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-historie-als-feit',
  label: 'Verwart historische feiten met meningen',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat controleerbare historische gebeurtenissen meningen zijn.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-aantallen-als-feiten',
  label: 'Verwart controleerbare aantallen met meningen',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat controleerbare getallen of aantallen meningen zijn.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-overtr-trap',
  label: 'Herkent overtreffende trap niet als meningssignaal',
  module: 'Taalbegrip',
  description: 'Leerling herkent niet dat de overtreffende trap (het mooiste, het beste) een waardeoordeel is.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-zou-moeten',
  label: 'Herkent "zou moeten" niet als meningssignaal',
  module: 'Taalbegrip',
  description: 'Leerling herkent niet dat "zou moeten" een persoonlijke overtuiging aangeeft.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-superlative',
  label: 'Herkent superlative niet als waardeoordeel',
  module: 'Taalbegrip',
  description: 'Leerling herkent niet dat "het fijnste", "het leukste" etc. waardeoordelen zijn.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-twee-feiten',
  label: 'Verwart twee feiten in één zin met feit+mening',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een zin met twee controleerbare feiten automatisch feit+mening bevat.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-ik-vind-geen-feit',
  label: 'Denkt dat "ik vind" automatisch feit+mening maakt',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "ik vind" in een zin automatisch betekent dat er ook een feit in staat.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-sterke-bewering',
  label: 'Verwart sterke bewering met feit+mening',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een sterke bewering (de beste) automatisch een feit bevat.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.signaal-opsomming-vs-mening',
  label: 'Verwart opsommingssignaalwoord met meningssignaalwoord',
  module: 'Taalbegrip',
  description: 'Leerling verwart "ten eerste" (opsomming) met een meningssignaalwoord.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.signaal-voorbeeld-vs-mening',
  label: 'Verwart voorbeeldsignaalwoord met meningssignaalwoord',
  module: 'Taalbegrip',
  description: 'Leerling verwart "bijvoorbeeld" (voorbeeld) met een meningssignaalwoord.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.signaal-uitleg-vs-mening',
  label: 'Verwart uitlegsignaalwoord met meningssignaalwoord',
  module: 'Taalbegrip',
  description: 'Leerling verwart "namelijk" (uitleg) met een meningssignaalwoord.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-data-als-feit',
  label: 'Verwart controleerbare data met mening',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat controleerbare data (jaartallen, getallen) meningen zijn.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-werkstuk-alles-feit',
  label: 'Denkt dat alles in een werkstuk een feit is',
  module: 'Taalbegrip',
  description: 'Leerling gaat ervan uit dat alles in een werkstuk automatisch een feit is.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
}, {
  id: 'taa.feit-mening-alles-mening',
  label: 'Denkt dat alles een mening is',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat alle uitspraken meningen zijn, ook controleerbare feiten.',
  typicalFamilies: ['TAA-LEZEN-FEIT']
},
// ─── Doel van de tekst (TAA-LEZEN-DOEL) ────────────────────────────────
{
  id: 'taa.doel-informeren-vs-overtuigen',
  label: 'Verwart informeren met overtuigen',
  module: 'Taalbegrip',
  description: 'Leerling herkent niet dat een oproep of mening wijst op overtuigen, niet informeren.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.doel-vermaken-vs-overtuigen',
  label: 'Verwart vermaken met overtuigen',
  module: 'Taalbegrip',
  description: 'Leerling verwart een overtuigende tekst met een vermakelijke tekst.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.doel-waarschuwen-vs-overtuigen',
  label: 'Verwart waarschuwen met overtuigen',
  module: 'Taalbegrip',
  description: 'Leerling verwart een overtuigende tekst met een waarschuwende tekst.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.doel-overtuigen-vs-informeren',
  label: 'Verwart overtuigen met informeren',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een neutrale informatieve tekst overtuigend is.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.doel-vermaken-vs-informeren',
  label: 'Verwart vermaken met informeren',
  module: 'Taalbegrip',
  description: 'Leerling verwart een informatieve tekst met een vermakelijke tekst.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.doel-instrueren-vs-informeren',
  label: 'Verwart instrueren met informeren',
  module: 'Taalbegrip',
  description: 'Leerling verwart een informatieve tekst met een instructietekst.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.signaal-opsomming-vs-overtuigen',
  label: 'Verwart opsommingssignaal met overtuigingssignaal',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "ten eerste" een overtuigingssignaalwoord is.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.signaal-verhaal-vs-overtuigen',
  label: 'Verwart verhaalsignaal met overtuigingssignaal',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "er was eens" een overtuigingssignaalwoord is.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.signaal-instructie-vs-overtuigen',
  label: 'Verwart instructiesignaal met overtuigingssignaal',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "voeg toe" een overtuigingssignaalwoord is.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.doel-neutraal-als-overtuigen-voor',
  label: 'Denkt dat voor+nadelen = overtuigen vóór',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een neutrale tekst met voor- en nadelen overtuigt vóór het onderwerp.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
}, {
  id: 'taa.doel-neutraal-als-overtuigen-tegen',
  label: 'Denkt dat voor+nadelen = overtuigen tegen',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een neutrale tekst met voor- en nadelen overtuigt tegen het onderwerp.',
  typicalFamilies: ['TAA-LEZEN-DOEL']
},
// ─── Samenvatten (TAA-LEZEN-SAMENVATTEN) ────────────────────────────────
{
  id: 'taa.samenvatting-te-smal',
  label: 'Kiest detail i.p.v. samenvatting',
  module: 'Taalbegrip',
  description: 'Leerling kiest een detail als samenvatting in plaats van de overkoepelende hoofdgedachte.',
  typicalFamilies: ['TAA-LEZEN-SAMENVATTEN']
}, {
  id: 'taa.samenvatting-hoofdgedachte-weglaten',
  label: 'Denkt dat hoofdgedachte niet in samenvatting hoort',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat de hoofdgedachte niet in een samenvatting thuishoort.',
  typicalFamilies: ['TAA-LEZEN-SAMENVATTEN']
}, {
  id: 'taa.samenvatting-punten-weglaten',
  label: 'Denkt dat belangrijkste punten niet in samenvatting horen',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat de belangrijkste punten per alinea niet in een samenvatting horen.',
  typicalFamilies: ['TAA-LEZEN-SAMENVATTEN']
}, {
  id: 'taa.samenvatting-conclusie-weglaten',
  label: 'Denkt dat conclusie niet in samenvatting hoort',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat de conclusie van de schrijver niet in een samenvatting hoort.',
  typicalFamilies: ['TAA-LEZEN-SAMENVATTEN']
}, {
  id: 'taa.samenvatting-eigen-mening',
  label: 'Denkt dat samenvatten begint met eigen mening',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat je bij samenvatten begint met je eigen mening opschrijven.',
  typicalFamilies: ['TAA-LEZEN-SAMENVATTEN']
}, {
  id: 'taa.samenvatting-alle-details',
  label: 'Denkt dat alle details opgeschreven moeten worden',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een samenvatting alle details moet bevatten.',
  typicalFamilies: ['TAA-LEZEN-SAMENVATTEN']
}, {
  id: 'taa.samenvatting-overschrijven',
  label: 'Verwart samenvatten met overschrijven',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat samenvatten hetzelfde is als de tekst letterlijk overschrijven.',
  typicalFamilies: ['TAA-LEZEN-SAMENVATTEN']
},
// ─── Tekststructuur (TAA-LEZEN-STRUCTUUR) ───────────────────────────────
{
  id: 'taa.structuur-oorzaak-vs-probleem',
  label: 'Verwart oorzaak-gevolg met probleem-oplossing',
  module: 'Taalbegrip',
  description: 'Leerling verwart de oorzaak-gevolgstructuur met de probleem-oplossingstructuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-vergelijking-vs-probleem',
  label: 'Verwart vergelijking met probleem-oplossing',
  module: 'Taalbegrip',
  description: 'Leerling verwart de vergelijkingsstructuur met de probleem-oplossingstructuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-chronologisch-vs-probleem',
  label: 'Verwart chronologisch met probleem-oplossing',
  module: 'Taalbegrip',
  description: 'Leerling verwart de chronologische structuur met de probleem-oplossingstructuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.signaal-opsomming-vs-oorzaak',
  label: 'Verwart opsommingssignaal met oorzaak-gevolg',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "ten eerste" een oorzaak-gevolg signaalwoord is.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.signaal-tegenstelling-vs-oorzaak',
  label: 'Verwart tegenstellingssignaal met oorzaak-gevolg',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "in tegenstelling tot" een oorzaak-gevolg signaalwoord is.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.signaal-chronologisch-vs-oorzaak',
  label: 'Verwart chronologisch signaal met oorzaak-gevolg',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "ten slotte" een oorzaak-gevolg signaalwoord is.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-opsomming-vs-vergelijking',
  label: 'Verwart opsomming met vergelijking',
  module: 'Taalbegrip',
  description: 'Leerling verwart de opsommingsstructuur met de vergelijkingsstructuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-probleem-vs-vergelijking',
  label: 'Verwart probleem-oplossing met vergelijking',
  module: 'Taalbegrip',
  description: 'Leerling verwart de probleem-oplossingstructuur met de vergelijkingsstructuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-oorzaak-vs-vergelijking',
  label: 'Verwart oorzaak-gevolg met vergelijking',
  module: 'Taalbegrip',
  description: 'Leerling verwart de oorzaak-gevolgstructuur met de vergelijkingsstructuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-oorzaak-vs-chronologisch',
  label: 'Verwart oorzaak-gevolg met chronologisch',
  module: 'Taalbegrip',
  description: 'Leerling verwart de oorzaak-gevolgstructuur met de chronologische structuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-vergelijking-vs-chronologisch',
  label: 'Verwart vergelijking met chronologisch',
  module: 'Taalbegrip',
  description: 'Leerling verwart de vergelijkingsstructuur met de chronologische structuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-probleem-vs-chronologisch',
  label: 'Verwart probleem-oplossing met chronologisch',
  module: 'Taalbegrip',
  description: 'Leerling verwart de probleem-oplossingstructuur met de chronologische structuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-signaal-verkeerde-categorie',
  label: 'Signaalwoord in verkeerde structuur-categorie geplaatst',
  module: 'Taalbegrip',
  description: 'Leerling sorteert een signaalwoord onder de verkeerde tekststructuur-categorie (opsomming/oorzaak-gevolg/tegenstelling/chronologisch).',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-chronologisch-kenmerk-verward',
  label: 'Kenmerk van andere structuur aan chronologisch toegeschreven',
  module: 'Taalbegrip',
  description: 'Leerling schrijft een kenmerk dat hoort bij een andere tekststructuur toe aan een chronologische structuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-niet-chronologisch-gemist',
  label: 'Chronologisch kenmerk niet herkend',
  module: 'Taalbegrip',
  description: 'Leerling herkent een chronologisch kenmerk niet als horend bij een chronologische tekststructuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-schema-vs-oorzaak',
  label: 'Schema-structuur verward met oorzaak-gevolg',
  module: 'Taalbegrip',
  description: 'Leerling negeert blok-labels in een schema en kiest oorzaak-gevolg als structuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-schema-vs-vergelijking',
  label: 'Schema-structuur verward met vergelijking',
  module: 'Taalbegrip',
  description: 'Leerling negeert pijl-richting in een schema en kiest vergelijking als structuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
}, {
  id: 'taa.structuur-schema-vs-chronologisch',
  label: 'Schema-structuur verward met chronologisch',
  module: 'Taalbegrip',
  description: 'Leerling negeert aantal blokken in een schema en kiest chronologisch als structuur.',
  typicalFamilies: ['TAA-LEZEN-STRUCTUUR']
},
// ─── Verbanden (TAA-LEZEN-VERB) ─────────────────────────────────────────
{
  id: 'taa.verb-tegenstelling-vs-oorzaak',
  label: 'Verwart tegenstelling met oorzaak-gevolg',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een tegenstellingssignaalwoord (maar, toch) een oorzaak-gevolg aangeeft.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-tegenstelling-vs-gevolg',
  label: 'Verwart tegenstelling met gevolg',
  module: 'Taalbegrip',
  description: 'Leerling kiest een gevolg-signaalwoord (dus) als tegenstelling.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-tegenstelling-vs-toevoeging',
  label: 'Verwart tegenstelling met toevoeging',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een toevoegingswoord (ook) een tegenstelling aangeeft.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-tegenstelling-vs-opsomming',
  label: 'Verwart tegenstelling met opsomming',
  module: 'Taalbegrip',
  description: 'Leerling verwart een tegenstellingsverband met een opsommingsverband.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-tegenstelling-vs-tijdsvolgorde',
  label: 'Verwart tegenstelling met tijdsvolgorde',
  module: 'Taalbegrip',
  description: 'Leerling verwart een tegenstellingsverband met een tijdsvolgordeverband.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-oorzaak-vs-tegenstelling',
  label: 'Verwart oorzaak-gevolg met tegenstelling',
  module: 'Taalbegrip',
  description: 'Leerling verwart een oorzaak-gevolgverband met een tegenstellingsverband.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-oorzaak-vs-vergelijking',
  label: 'Verwart oorzaak-gevolg met vergelijking',
  module: 'Taalbegrip',
  description: 'Leerling verwart een oorzaak-gevolgverband met een vergelijkingsverband.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-oorzaak-vs-opsomming',
  label: 'Verwart oorzaak-gevolg met opsomming',
  module: 'Taalbegrip',
  description: 'Leerling verwart een oorzaak-gevolgverband met een opsommingsverband.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-opsomming-vs-tegenstelling',
  label: 'Verwart opsomming met tegenstelling',
  module: 'Taalbegrip',
  description: 'Leerling verwart een opsommingsverband met een tegenstellingsverband.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-opsomming-vs-oorzaak',
  label: 'Verwart opsomming met oorzaak-gevolg',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een opsommingssignaalwoord een oorzaak-gevolg aangeeft.',
  typicalFamilies: ['TAA-LEZEN-VERB']
}, {
  id: 'taa.verb-opsomming-vs-tijdsvolgorde',
  label: 'Verwart opsomming met tijdsvolgorde',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een opsommingssignaalwoord een tijdsvolgorde aangeeft.',
  typicalFamilies: ['TAA-LEZEN-VERB']
},
// ─── Verwijswoorden (TAA-LEZEN-VERW) ────────────────────────────────────
{
  id: 'taa.verw-daar-naar-plaats',
  label: 'Verwart plaatsverwijswoord met persoonsverwijzing',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "daar" naar een persoon verwijst in plaats van naar een plaats.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-daar-naar-object',
  label: 'Verwart plaatsverwijswoord met objectverwijzing',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "daar" naar een voorwerp verwijst in plaats van naar een plaats.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-ze-naar-plaats',
  label: 'Verwart persoonsverwijzing met plaatsverwijzing',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "ze" naar een plaats verwijst in plaats van naar personen.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-ze-naar-tijd',
  label: 'Verwart persoonsverwijzing met tijdsaanduiding',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "ze" naar een tijdsaanduiding verwijst.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-hem-naar-onderwerp',
  label: 'Verwart lijdend-voorwerp-verwijzing met onderwerpverwijzing',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "hem" naar het onderwerp verwijst terwijl "hij" dat al doet.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-hem-niet-in-tekst',
  label: 'Verwijst naar woord buiten de tekst',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een verwijswoord naar iets verwijst dat niet in de tekst staat.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-dat-naar-persoon',
  label: 'Verwart "dat" met persoonsverwijzing',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "dat" naar een persoon verwijst in plaats van naar een handeling.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-dat-naar-object-ipv-handeling',
  label: 'Verwart "dat" als objectverwijzing i.p.v. handelingsverwijzing',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "dat" naar een enkel object verwijst terwijl het naar een hele handeling verwijst.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-zij-enkelvoud-vs-meervoud',
  label: 'Verwart "zij" enkelvoud met meervoud',
  module: 'Taalbegrip',
  description: 'Leerling herkent niet dat "zij" zowel enkelvoud (vrouwelijk) als meervoud kan zijn.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-zij-naar-niet-persoon',
  label: 'Verwart "zij" met niet-persoonsverwijzing',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "zij" naar een voorwerp of begrip verwijst in plaats van naar personen.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-ander-zinsdeel',
  label: 'Verwijswoord aan verkeerd zinsdeel gekoppeld',
  module: 'Taalbegrip',
  description: 'Leerling koppelt een verwijswoord aan een ander zinsdeel dan het bedoelde antecedent.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-ander-onderwerp',
  label: 'Verwijswoord aan ander onderwerp gekoppeld',
  module: 'Taalbegrip',
  description: 'Leerling koppelt een verwijswoord aan een ander persoon of onderwerp dan in de tekst bedoeld is.',
  typicalFamilies: ['TAA-LEZEN-VERW']
}, {
  id: 'taa.verw-niet-in-tekst',
  label: 'Verwijswoord aan iets buiten de tekst gekoppeld',
  module: 'Taalbegrip',
  description: 'Leerling koppelt een verwijswoord aan iets dat niet in de tekst genoemd is.',
  typicalFamilies: ['TAA-LEZEN-VERW']
},
// ─── Voorspellen (TAA-LEZEN-VOORSPEL) ───────────────────────────────────
{
  id: 'taa.voorspel-enkele-aanwijzing',
  label: 'Baseert voorspelling op één aanwijzing',
  module: 'Taalbegrip',
  description: 'Leerling baseert de voorspelling op slechts één aanwijzing en negeert de overige.',
  typicalFamilies: ['TAA-LEZEN-VOORSPEL']
}, {
  id: 'taa.voorspel-aanwijzing-negeren',
  label: 'Negeert relevante aanwijzingen',
  module: 'Taalbegrip',
  description: 'Leerling negeert een of meer belangrijke aanwijzingen in de tekst bij het voorspellen.',
  typicalFamilies: ['TAA-LEZEN-VOORSPEL']
}, {
  id: 'taa.voorspel-genre-verwarring',
  label: 'Genre verkeerd voorspeld',
  module: 'Taalbegrip',
  description: 'Leerling voorspelt het verkeerde tekstgenre (bijv. instructie, sprookje) op basis van een titel of openingsregel.',
  typicalFamilies: ['TAA-LEZEN-VOORSPEL']
}, {
  id: 'taa.voorspel-titel-letterlijk',
  label: 'Titel te letterlijk genomen',
  module: 'Taalbegrip',
  description: 'Leerling neemt de titel letterlijk en mist de figuurlijke of overdrachtelijke betekenis.',
  typicalFamilies: ['TAA-LEZEN-VOORSPEL']
}, {
  id: 'taa.voorspel-extreme-conclusie',
  label: 'Trekt een te extreme conclusie',
  module: 'Taalbegrip',
  description: 'Leerling kiest een extreme of onwaarschijnlijke voorspelling in plaats van de meest logische.',
  typicalFamilies: ['TAA-LEZEN-VOORSPEL']
}, {
  id: 'taa.voorspel-negatief-bias',
  label: 'Negatieve bias bij voorspellen',
  module: 'Taalbegrip',
  description: 'Leerling verwacht een negatieve uitkomst terwijl de aanwijzingen positief zijn.',
  typicalFamilies: ['TAA-LEZEN-VOORSPEL']
}, {
  id: 'taa.voorspel-niet-in-tekst',
  label: 'Voorspelling niet gebaseerd op tekst',
  module: 'Taalbegrip',
  description: 'Leerling maakt een voorspelling die niet ondersteund wordt door aanwijzingen in de tekst.',
  typicalFamilies: ['TAA-LEZEN-VOORSPEL']
},
// ─── Conclusie (TAA-LEZEN-CONCLUSIE) ────────────────────────────────────
{
  id: 'taa.conclusie-correct',
  label: 'Conclusie correct getrokken',
  module: 'Taalbegrip',
  description: 'Tag voor het juiste antwoord bij conclusievragen: leerling trekt een conclusie die geldig volgt uit de tekst.',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
}, {
  id: 'taa.conclusie-detail-vs-conclusie',
  label: 'Verwart detail met conclusie',
  module: 'Taalbegrip',
  description: 'Leerling kiest een detail uit de tekst als conclusie i.p.v. een een logisch gevolg van de feiten.',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
}, {
  id: 'taa.conclusie-exclusiviteit',
  label: 'Trekt exclusieve conclusie ("alleen")',
  module: 'Taalbegrip',
  description: 'Leerling trekt een exclusieve conclusie ("alleen X") die niet door de feiten ondersteund wordt.',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
},
// ─── Conclusie — seed-reconciliatie (Phase B, juni 2026) ────────────────
{
  id: 'taa.conclusie-overgeneralisatie',
  label: 'Conclusie overgegeneraliseerd',
  module: 'Taalbegrip',
  description: 'Leerling trekt een te brede conclusie ("altijd", "iedereen", "het beste") die verder gaat dan wat de feiten in de tekst ondersteunen.',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
}, {
  id: 'taa.conclusie-geldig-vs-ongeldig',
  label: 'Geldige vs. ongeldige conclusie verward',
  module: 'Taalbegrip',
  description: 'Leerling herkent niet welk gegeven wel of niet bijdraagt aan de conclusie, of verwart een feit met een conclusie.',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
}, {
  id: 'taa.conclusie-voorspelling-vs-conclusie',
  label: 'Voorspelling verward met conclusie',
  module: 'Taalbegrip',
  description: 'Leerling kiest een voorspelling over de toekomst i.p.v. een conclusie die volgt uit de gegeven feiten.',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
}, {
  id: 'taa.conclusie-altijd-waar',
  label: 'Denkt dat conclusies altijd waar zijn',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een conclusie automatisch waar is en controleert niet of de premissen haar ondersteunen.',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
}, {
  id: 'taa.conclusie-mening-vs-conclusie',
  label: 'Mening verward met conclusie',
  module: 'Taalbegrip',
  description: 'Leerling kiest een mening of subjectief oordeel i.p.v. een onderbouwde conclusie die uit de tekstfeiten volgt.',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
}, {
  id: 'taa.conclusie-niet-in-tekst',
  label: 'Conclusie niet in tekst',
  module: 'Taalbegrip',
  description: 'Leerling kiest een conclusie die niet ondersteund wordt door de feiten in de tekst (zoals een irrelevant detail of een ongerelateerd gevolg).',
  typicalFamilies: ['TAA-LEZEN-CONCLUSIE']
},
// ─── Tekstsoort — seed-upgrade (Phase B, juni 2026) ─────────────────────
{
  id: 'taa.tekstsoort-informatief-als-verhalend',
  label: 'Informatieve tekst verward met verhalend',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een informatieve tekst (uitleg, kennisoverdracht) als verhalend.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-betoog-als-verhalend',
  label: 'Betoog verward met verhalend',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een betoog (overtuigend, met argumenten) als verhalend.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-instructie-als-verhalend',
  label: 'Instructie verward met verhalend',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een instructie (stappenplan, handleiding) als verhalend.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-sprookje-als-instructie',
  label: 'Sprookje verward met instructie',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een sprookje (verhalend) als instructie.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-informatief-als-instructie',
  label: 'Informatieve tekst verward met instructie',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een informatieve tekst als instructie.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-betoog-als-instructie',
  label: 'Betoog verward met instructie',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een betoog als instructie.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-sprookje-als-informatief',
  label: 'Sprookje verward met informatieve tekst',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een sprookje (verhalend) als informatief.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-instructie-als-informatief',
  label: 'Instructie verward met informatieve tekst',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een instructie (stappenplan) als informatief.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-gedicht-als-informatief',
  label: 'Gedicht verward met informatieve tekst',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een gedicht (literair/verhalend) als informatief.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-informatief-als-betoog',
  label: 'Informatieve tekst verward met betoog',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een informatieve tekst (neutraal) als betoog (overtuigend).',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-instructie-als-betoog',
  label: 'Instructie verward met betoog',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een instructie als betoog.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}, {
  id: 'taa.tekstsoort-verhalend-als-betoog',
  label: 'Verhalende tekst verward met betoog',
  module: 'Taalbegrip',
  description: 'Leerling classificeert een verhalende tekst als betoog.',
  typicalFamilies: ['TAA-TEKSTSOORT']
}];